import { useEffect } from 'react'
import { Button as MantineButton, Card, Center, Flex, Loader, Select, SimpleGrid, Stack, Text, TextInput } from '@mantine/core'
import { IconCheck, IconRefresh, IconX } from '@tabler/icons-react'
import { Button } from 'components/Button'
import { notifications } from 'context'
import { useSchoolDetails } from '../hooks/useSchoolDetails'
import { US_STATES } from '../data/usStates'

export function SchoolDetailsForm() {
  const {
    form, errors, cityOptions, loading, loadError, saving,
    updateField, handleStateChange, handleSave,
  } = useSchoolDetails()

  useEffect(() => {
    if (loadError) {
      notifications.show({
        message: 'Failed to load school details. Please try again.',
        color: 'red',
        icon: <IconX size={18} />,
      })
    }
  }, [loadError])

  if (loading) {
    return (
      <Card withBorder radius={6} sx={{ padding: '18px 24px' }}>
        <Center py='xl'><Loader size='sm' /></Center>
      </Card>
    )
  }

  if (loadError) {
    return (
      <Card withBorder radius={6} sx={{ padding: '18px 24px' }}>
        <Center py='xl'>
          <Stack align='center' spacing='sm'>
            <Text color='dimmed' size='sm'>Could not load school details.</Text>
            <MantineButton
              variant='subtle'
              size='sm'
              leftIcon={<IconRefresh size={16} />}
              onClick={() => window.location.reload()}
            >
              Reload page
            </MantineButton>
          </Stack>
        </Center>
      </Card>
    )
  }

  async function onSave() {
    const success = await handleSave()
    notifications.show({
      message: success ? 'School details updated successfully!' : 'Failed to update school details.',
      color: success ? 'green' : 'red',
      icon: success ? <IconCheck size={18} /> : <IconX size={18} />,
    })
  }

  return (
    <Card withBorder radius={6} sx={{ padding: '18px 24px' }}>
      <Text fw={600} size='lg' mb='md'>School information</Text>

      <Flex direction='column' gap='md'>
        <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
          <TextInput
            size='md'
            label='School Name'
            placeholder='Enter school name'
            value={form.name}
            onChange={e => updateField('name', e.currentTarget.value)}
            error={errors.name}
          />
          <TextInput
            size='md'
            label='Zipcode'
            placeholder='Enter zipcode'
            value={form.zipcode}
            onChange={e => updateField('zipcode', e.currentTarget.value)}
          />
        </SimpleGrid>

        <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
          <TextInput
            size='md'
            label='Country'
            value='United States'
            disabled
          />
          <Select
            size='md'
            label='State'
            placeholder='Select a state'
            data={US_STATES}
            value={form.state}
            onChange={handleStateChange}
            searchable
          />
        </SimpleGrid>

        <Select
          size='md'
          label='City'
          placeholder={form.state ? 'Select a city' : 'Select a state first'}
          data={cityOptions}
          value={form.cityId}
          onChange={value => updateField('cityId', value)}
          searchable
          disabled={!form.state}
          sx={{ maxWidth: '50%' }}
        />

        <Flex justify='flex-end' mt='sm'>
          <Button variant='filled' color='turquoise' onClick={onSave} loading={saving}>
            Save
          </Button>
        </Flex>
      </Flex>
    </Card>
  )
}
